import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  InputAdornment,
  Chip,
  Tooltip,
  IconButton,
  Card,
  CardContent,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  ShoppingCart as CartIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { 
  useGetInventoriesQuery, 
  useBulkAdjustInventoryMutation, 
  useGetBulkAdjustmentHistoryQuery,
  useGetBulkAdjustmentDetailsQuery 
} from '../services/inventory.service';
import { useGetLocationsQuery } from '../services/locations.service';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { Role } from '../types';

interface BulkAdjustmentItem {
  productId: string;
  locationId: string;
  productName: string;
  locationName: string;
  productCode: string;
  type: 'SET' | 'ADD' | 'SUBTRACT';
  quantity: number;
  currentQuantity: number;
}

const BulkAdjustmentPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [adjustments, setAdjustments] = useState<BulkAdjustmentItem[]>([]);

  // Get current user from Redux store
  const { user } = useSelector((state: RootState) => state.auth);
  const [reason, setReason] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [defaultAdjustmentType, setDefaultAdjustmentType] = useState<'SET' | 'ADD' | 'SUBTRACT'>('SET');
  const [selectedHistoryCode, setSelectedHistoryCode] = useState<string | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  // Debounce search term - wait 300ms after user stops typing
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length >= 2 || searchTerm.length === 0) {
        setDebouncedSearchTerm(searchTerm);
      }
    }, 300); // Wait 300ms as requested

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch data
  const { data: locationsResponse } = useGetLocationsQuery({ active: true });
  const locations = locationsResponse?.data || [];

  const {
    data: inventoryResponse,
    isLoading: isLoadingInventory,
    error: inventoryError,
    refetch: refetchInventory
  } = useGetInventoriesQuery({
    page: 1,
    limit: 50,
    search: debouncedSearchTerm || undefined,
    locationId: selectedLocation || undefined,
  });

  const [bulkAdjustInventory, { isLoading: isSubmitting }] = useBulkAdjustInventoryMutation();

  // History queries
  const { 
    data: historyResponse, 
    isLoading: isLoadingHistory,
    refetch: refetchHistory 
  } = useGetBulkAdjustmentHistoryQuery({
    page: 1,
    limit: 10,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  const { 
    data: detailsResponse, 
    isLoading: isLoadingDetails 
  } = useGetBulkAdjustmentDetailsQuery(selectedHistoryCode!, {
    skip: !selectedHistoryCode
  });

  const inventories = inventoryResponse?.data?.inventory || [];
  const historyData = historyResponse?.data || [];
  const detailsData = detailsResponse?.data;

  const addToAdjustments = (item: any, locationId: string, locationName: string, currentQuantity: number) => {
    const existingIndex = adjustments.findIndex(
      adj => adj.productId === item.productId && adj.locationId === locationId
    );

    if (existingIndex >= 0) {
      alert('Este producto y ubicación ya están en la lista de ajustes');
      return;
    }

    const newAdjustment: BulkAdjustmentItem = {
      productId: item.productId,
      locationId,
      productName: item.product?.name || 'Producto desconocido',
      locationName,
      productCode: item.product?.code || item.product?.sku || 'Sin código',
      type: defaultAdjustmentType,
      quantity: defaultAdjustmentType === 'SET' ? currentQuantity : 0,
      currentQuantity
    };

    setAdjustments(prev => [...prev, newAdjustment]);
  };

  const updateAdjustment = (index: number, field: keyof BulkAdjustmentItem, value: any) => {
    setAdjustments(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeAdjustment = (index: number) => {
    setAdjustments(prev => prev.filter((_, i) => i !== index));
  };

  const clearAllAdjustments = () => {
    setAdjustments([]);
  };

  const handleSubmit = async () => {
    if (adjustments.length === 0) {
      alert('Debe agregar al menos un ajuste');
      return;
    }

    try {
      const adjustmentData = adjustments.map(adj => ({
        productId: adj.productId,
        locationId: adj.locationId,
        type: adj.type,
        quantity: Number(adj.quantity)
      }));

      const result = await bulkAdjustInventory({
        adjustments: adjustmentData,
        reason: reason || 'Ajuste masivo'
      }).unwrap();

      setShowConfirmDialog(false);
      setAdjustments([]);
      setReason('');
      refetchInventory();
      refetchHistory(); // Refresh history after successful adjustment
      
      // Show results with adjustment code
      const message = `Ajustes completados (Código: ${result.data.bulkAdjustmentCode}):\n${result.data.successfulAdjustments} exitosos\n${result.data.failedAdjustments} fallidos`;
      
      if (result.data.errors.length > 0) {
        const errorDetails = result.data.errors.map(err => `• ${err.error}`).join('\n');
        alert(`${message}\n\nErrores:\n${errorDetails}`);
      } else {
        alert(message);
      }
    } catch (error: any) {
      console.error('Error in bulk adjustment:', error);
      const errorMessage = error?.data?.message || 'Error al realizar ajustes masivos. Por favor, intente de nuevo.';
      alert(errorMessage);
    }
  };

  const calculateFinalQuantity = (adjustment: BulkAdjustmentItem) => {
    switch (adjustment.type) {
      case 'SET':
        return adjustment.quantity;
      case 'ADD':
        return adjustment.currentQuantity + adjustment.quantity;
      case 'SUBTRACT':
        return adjustment.currentQuantity - adjustment.quantity;
      default:
        return adjustment.currentQuantity;
    }
  };

  const handleViewDetails = (code: string) => {
    setSelectedHistoryCode(code);
    setShowDetailsDialog(true);
  };

  const handleCloseDetails = () => {
    setShowDetailsDialog(false);
    setSelectedHistoryCode(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (inventoryError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar el inventario
        </Alert>
        <Button variant="contained" onClick={() => refetchInventory()}>
          Reintentar
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexShrink: 0 }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Ajuste Masivo de Inventario
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Busque productos, agregue cantidades y confirme todos los ajustes de una vez
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetchInventory()}
            disabled={isLoadingInventory}
          >
            Actualizar
          </Button>
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={clearAllAdjustments}
            disabled={adjustments.length === 0}
            color="warning"
          >
            Limpiar Todo
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() => setShowConfirmDialog(true)}
            disabled={adjustments.length === 0 || isSubmitting}
          >
            Guardar {adjustments.length} Ajustes
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexGrow: 1, overflow: 'hidden' }}>
        {/* Left Panel - Search and Add Products */}
        <Box sx={{ flex: 0.4, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Paper sx={{ p: 3, mb: 2, flexShrink: 0 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Buscar y Agregar Productos
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                placeholder="Buscar productos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Filtrar por Ubicación</InputLabel>
                <Select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  label="Filtrar por Ubicación"
                >
                  <MenuItem value="">Todas las ubicaciones</MenuItem>
                  {locations.map(loc => (
                    <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Tipo de Ajuste por Defecto</InputLabel>
                <Select
                  value={defaultAdjustmentType}
                  onChange={(e) => setDefaultAdjustmentType(e.target.value as 'SET' | 'ADD' | 'SUBTRACT')}
                  label="Tipo de Ajuste por Defecto"
                >
                  <MenuItem value="SET">Establecer Cantidad</MenuItem>
                  <MenuItem value="ADD">Agregar Stock</MenuItem>
                  {user?.role !== Role.CASHIER && (
                    <MenuItem value="SUBTRACT">Reducir Stock</MenuItem>
                  )}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Razón del Ajuste"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Describe la razón..."
              />
            </Box>
          </Paper>

          {/* Search Results */}
          <Paper sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6">
                Resultados de Búsqueda
                {isLoadingInventory && <CircularProgress size={20} sx={{ ml: 1 }} />}
              </Typography>
            </Box>
            
            <TableContainer sx={{ flexGrow: 1 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Producto</TableCell>
                    <TableCell>Ubicaciones</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inventories.map((item, index) => (
                    <TableRow key={item.productId || index} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {item.product?.name || 'Producto desconocido'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.product?.code || item.product?.sku || 'Sin código'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {item.locations
                            ?.filter((loc: any) => !selectedLocation || loc.locationId === selectedLocation)
                            ?.map((loc: any, locIndex: number) => (
                            <Tooltip 
                              key={locIndex} 
                              title={`Stock: ${loc.quantity}`}
                              placement="top"
                            >
                              <Chip
                                size="small"
                                label={`${loc.locationName}: ${loc.quantity}`}
                                variant="outlined"
                                clickable
                                onClick={() => addToAdjustments(item, loc.locationId, loc.locationName, loc.quantity)}
                                sx={{ fontSize: '0.75rem' }}
                              />
                            </Tooltip>
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => {
                            item.locations
                              ?.filter((loc: any) => !selectedLocation || loc.locationId === selectedLocation)
                              ?.forEach((loc: any) => {
                              addToAdjustments(item, loc.locationId, loc.locationName, loc.quantity);
                            });
                          }}
                          variant="outlined"
                        >
                          Agregar {selectedLocation ? '' : 'Todas'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>

        {/* Middle Panel - Adjustment Cart */}
        <Box sx={{ flex: 0.35, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CartIcon sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Ajustes Programados ({adjustments.length})
                </Typography>
              </Box>

              {adjustments.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                  <Typography variant="body1" color="text.secondary" textAlign="center">
                    No hay ajustes agregados.<br />
                    Use la búsqueda para encontrar productos y haga clic en "Agregar"
                  </Typography>
                </Box>
              ) : (
                <TableContainer sx={{ flexGrow: 1 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Producto</TableCell>
                        <TableCell>Ubicación</TableCell>
                        <TableCell>Actual</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Cantidad</TableCell>
                        <TableCell>Final</TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {adjustments.map((adjustment, index) => {
                        const finalQuantity = calculateFinalQuantity(adjustment);
                        
                        return (
                          <TableRow key={index}>
                            <TableCell>
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {adjustment.productName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {adjustment.productCode}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {adjustment.locationName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {adjustment.currentQuantity}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <FormControl size="small" sx={{ minWidth: 80 }}>
                                <Select
                                  value={adjustment.type}
                                  onChange={(e) => updateAdjustment(index, 'type', e.target.value)}
                                >
                                  <MenuItem value="SET">set</MenuItem>
                                  <MenuItem value="ADD">agregar</MenuItem>
                                  {user?.role !== Role.CASHIER && (
                                    <MenuItem value="SUBTRACT">reducir</MenuItem>
                                  )}
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                type="number"
                                value={adjustment.quantity}
                                onChange={(e) => updateAdjustment(index, 'quantity', Number(e.target.value))}
                                inputProps={{ min: 0 }}
                                sx={{ width: 80 }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography 
                                variant="body2" 
                                color={finalQuantity < 0 ? 'error' : 'inherit'}
                                fontWeight={finalQuantity < 0 ? 'bold' : 'normal'}
                              >
                                {finalQuantity}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => removeAdjustment(index)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Right Panel - History */}
        <Box sx={{ flex: 0.25, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <HistoryIcon sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Historial de Ajustes
                </Typography>
              </Box>

              {isLoadingHistory ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : historyData.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    No hay historial de ajustes masivos
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                  {historyData.map((item) => (
                    <Card 
                      key={item.id} 
                      sx={{ 
                        mb: 1, 
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                      onClick={() => handleViewDetails(item.code)}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle2" color="primary">
                            {item.code}
                          </Typography>
                          <IconButton size="small">
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {formatDate(item.date)}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {item.user.name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                          <Chip
                            size="small"
                            label={`${item.successfulAdjustments}/${item.totalAdjustments}`}
                            color={item.failedAdjustments > 0 ? 'warning' : 'success'}
                            icon={item.failedAdjustments > 0 ? <ErrorIcon /> : <CheckCircleIcon />}
                          />
                        </Box>
                        {item.reason && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {item.reason.length > 40 ? `${item.reason.substring(0, 40)}...` : item.reason}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog 
        open={showConfirmDialog} 
        onClose={() => setShowConfirmDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Confirmar Ajustes Masivos
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            ¿Está seguro que desea procesar {adjustments.length} ajustes de inventario?
          </Typography>
          
          {reason && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong>Razón:</strong> {reason}
            </Typography>
          )}

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Resumen de ajustes:
          </Typography>
          
          <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Producto</TableCell>
                  <TableCell>Ubicación</TableCell>
                  <TableCell>Actual → Final</TableCell>
                  <TableCell>Tipo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {adjustments.map((adjustment, index) => (
                  <TableRow key={index}>
                    <TableCell>{adjustment.productName}</TableCell>
                    <TableCell>{adjustment.locationName}</TableCell>
                    <TableCell>
                      {adjustment.currentQuantity} → {calculateFinalQuantity(adjustment)}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={adjustment.type} 
                        size="small"
                        color={
                          adjustment.type === 'ADD' ? 'success' : 
                          adjustment.type === 'SUBTRACT' ? 'error' : 
                          'default'
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>
            Cancelar
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Procesando...' : 'Confirmar Ajustes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Adjustment Details Dialog */}
      <Dialog
        open={showDetailsDialog}
        onClose={handleCloseDetails}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Detalles del Ajuste Masivo
            </Typography>
            {detailsData && (
              <Chip
                label={detailsData.code}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {isLoadingDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : detailsData ? (
            <Box>
              {/* Summary Information */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Información General
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Usuario:</Typography>
                    <Typography variant="body1">{detailsData.user.name}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Fecha:</Typography>
                    <Typography variant="body1">{formatDate(detailsData.createdAt)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Estado:</Typography>
                    <Chip 
                      label={detailsData.status} 
                      size="small"
                      color={detailsData.status === 'COMPLETED' ? 'success' : 'default'}
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Ubicaciones Afectadas:</Typography>
                    <Typography variant="body1">{detailsData.statistics.locationsCovered}</Typography>
                  </Box>
                </Box>
                {detailsData.reason && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">Razón:</Typography>
                    <Typography variant="body1">{detailsData.reason}</Typography>
                  </Box>
                )}
              </Box>

              {/* Statistics */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Estadísticas
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip
                    label={`${detailsData.statistics.successfulItems} Exitosos`}
                    color="success"
                    variant="outlined"
                    icon={<CheckCircleIcon />}
                  />
                  <Chip
                    label={`${detailsData.statistics.failedItems} Fallidos`}
                    color="error"
                    variant="outlined"
                    icon={<ErrorIcon />}
                  />
                  <Chip
                    label={`${detailsData.statistics.totalQuantityChanged} Unidades Cambiadas`}
                    variant="outlined"
                  />
                </Box>
              </Box>

              {/* Detailed Items */}
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Detalle de Ajustes
                </Typography>
                <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Producto</TableCell>
                        <TableCell>Ubicación</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell align="right">Anterior</TableCell>
                        <TableCell align="right">Cambio</TableCell>
                        <TableCell align="right">Nuevo</TableCell>
                        <TableCell>Estado</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detailsData.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {item.product.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {item.product.code}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>{item.location.name}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={item.type}
                              color={
                                item.type === 'ADD' ? 'success' :
                                item.type === 'SUBTRACT' ? 'error' :
                                'default'
                              }
                            />
                          </TableCell>
                          <TableCell align="right">{item.previousQuantity}</TableCell>
                          <TableCell align="right">
                            {item.type === 'SET' ? item.quantity :
                             item.type === 'ADD' ? `+${item.quantity}` :
                             `-${item.quantity}`}
                          </TableCell>
                          <TableCell align="right">{item.newQuantity}</TableCell>
                          <TableCell>
                            {item.success ? (
                              <Chip
                                size="small"
                                label="Exitoso"
                                color="success"
                                icon={<CheckCircleIcon />}
                              />
                            ) : (
                              <Tooltip title={item.error || 'Error desconocido'}>
                                <Chip
                                  size="small"
                                  label="Error"
                                  color="error"
                                  icon={<ErrorIcon />}
                                />
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          ) : (
            <Alert severity="error">
              No se pudieron cargar los detalles del ajuste masivo
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BulkAdjustmentPage;